<template>
    <div v-if="place" class="place-detail-page page">
        <div class="container">
            <NavigationsBreadcrumb :items="breadcrumbs" class="mt-0" />

            <h1 class="pt-2">{{ $t(`pickupPlace.${placeType}`) }}: {{ place.name }}</h1>

            <div class="row">
                <div class="col-12 col-md-3 d-flex flex-column">
                    <h2>{{ $t('pickupPlace.contactInfo') }}</h2>

                    <div class="d-flex">
                        <i class="ico ico-map-place-geo"></i>
                        <div>
                            <div class="fs-14">
                                <div v-if="place.contact.place">{{ place.contact.place }}<br /></div>
                                <div v-if="place.contact.street">{{ place.contact.street }}<br /></div>
                                <div v-if="place.contact.city">{{ place.contact.city }}<br /></div>
                                <div v-if="place.contact.zip">{{ place.contact.zip }}</div>
                            </div>

                            <div class="mt-2">
                                <a
                                    class="place-detail-page__location"
                                    :href="$t('pickupPlace.location') + place.location.latitude + ',' + place.location.longitude"
                                    target="_blank"
                                >
                                    {{ $t('pickupPlace.showOnMap') }}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div v-if="place.directions" v-html="place.directions"></div>
                    <div v-if="place.directionsCar" v-html="place.directionsCar"></div>
                </div>

                <div class="col-12 col-md-3 d-flex flex-column">
                    <h2 class="mt-3">
                        {{ $t('pickupPlace.openingHours') }}
                    </h2>

                    <table class="table table-border-bottom">
                        <tr>
                            <td colspan="2">
                                <div class="place-detail-page__circle" :class="place.isOpen ? 'ico-is-opened' : 'ico-is-closed'"></div>
                                <div :class="place.isOpen ? 'text-success' : 'text-danger'" class="place-detail-page__circle-text">
                                    <div v-if="place.isOpen">
                                        {{ $t('pickupPlace.opened') }}
                                    </div>
                                    <div v-else>
                                        {{ $t('pickupPlace.closed') }}
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <template v-for="(hours, day) in place.openingTime" :key="day">
                            <tr v-if="day !== 'exception'" :class="getDayName() === day ? 'table-success' : ''">
                                <td>
                                    {{ $t(`days.${day}`) }}
                                    <span v-if="getDayName() === day" class="place-detail-page__badge badge badge-success">
                                        {{ $t('pickupPlace.today') }}
                                    </span>
                                </td>
                                <td v-html="hours || $t('pickupPlace.closed')"></td>
                            </tr>
                        </template>
                        <tr v-if="place.openingTime['exception']">
                            <td colspan="2">{{ place.openingTime['exception'] }}</td>
                        </tr>
                    </table>
                </div>

                <div class="col-12 col-md-6">
                    <div>
                        <h2 class="mt-3">
                            {{ $t(`pickupPlace.${placeType}`) + ' ' + $t('pickupPlace.onMap') }}
                        </h2>

                        <div class="place-detail-page__map-container">
                            <LMap
                                ref="map"
                                :zoom="16"
                                :min-zoom="3"
                                :center="[place.location.latitude, place.location.longitude]"
                                :use-global-leaflet="false"
                                :options="{ zoomControl: false }"
                            >
                                <LControl position="bottomleft">
                                    <a href="https://mapy.cz/" target="_blank">
                                        <img src="https://api.mapy.cz/img/api/logo.svg" alt="Mapy.cz" height="30" />
                                    </a>
                                </LControl>
                                <LTileLayer
                                    :url="`https://api.mapy.cz/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${mapyCzApiKey}`"
                                    :attribution="mapsAttribution"
                                    layer-type="base"
                                    name="OpenStreetMap"
                                />

                                <LMarker :lat-lng="[place.location.latitude, place.location.longitude]">
                                    <LIcon
                                        :icon-url="`https://www.pilulka.cz/nuxt-assets/icons/map-ico-${placeType}.png`"
                                        :icon-size="[30, 43]"
                                    />
                                </LMarker>

                                <LControlZoom position="bottomright" />
                            </LMap>
                        </div>
                    </div>
                </div>
            </div>

            <hr class="place-detail-page__hr" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRoute } from '#imports'
import PickupPlace from '~/models/Api/Cart/PickupPlace'
import BreadcrumbItem from '~/models/BreadcrumbItem'
import getDayName from '~/extensions/getDayName'
import PilulkaHeadInfo from '~/models/Head/PilulkaHeadInfo'

const props = defineProps<{
    placeId: number
    shippingType: string
    placeType: string
}>()

const mapyCzApiKey = useRuntimeConfig().public.mapyCz.apiKey
const mapsAttribution = '<a href="https://api.mapy.cz/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>'

const route = useRoute()
const nuxtApp = useNuxtApp()
const breadcrumbs = ref<BreadcrumbItem[]>([])
const place = ref<PickupPlace | null>(null)

const response = await nuxtApp.$repository.viewPickupPlace(props.placeId, props.shippingType)
place.value = response.response ?? null

const i18n = useI18n()
breadcrumbs.value = [
    {
        title: i18n.t('pickupPlace.mapTitle'),
        url: i18n.t('pickupPlace.mapLink') + props.shippingType,
    },
    {
        title: place.value?.name ?? '',
        url: route.path,
    },
]

const config = useRuntimeConfig()
const headInfo = route.meta.headInfo as PilulkaHeadInfo
const defaultCanonical = `${config.public.apiUrl}${place.value?.url ?? ''}`
useHead({
    title: headInfo.title !== '' ? headInfo.title : place.value?.name ?? '',
    bodyAttrs: {
        'data-no-cache': true,
    },
    meta: [
        {
            hid: 'description',
            name: 'description',
            content: place.value?.contact.placeDetailed ?? '',
        },
    ],
    link: [
        {
            rel: 'canonical',
            href: headInfo.canonical !== '' ? headInfo.canonical : defaultCanonical,
        },
    ],
})
</script>

<style lang="scss" scoped>
.place-detail-page {
    #js-place-detail-map {
        width: 100%;
        height: 100%;
        min-height: 280px;
    }

    &__hr {
        border-top: 1px solid $color-grey-dark;
    }

    &__circle {
        height: 15px;
        width: 15px;
        border-radius: 50%;
        display: inline-block;
    }

    &__circle-text {
        display: inline-block;
        font-size: 16px;
        font-weight: bold;
        margin-left: 15px;
    }

    &__badge {
        padding: 0.25rem 0.5rem;
        margin-left: 0.5rem;
        text-transform: uppercase;
        border-radius: 5px;
    }

    .ico-is-opened {
        background-color: #4eae33;
    }

    .ico-is-closed {
        background-color: $color-grey-semi-dark;
    }

    .ico-map-place-geo {
        height: 24px;
        width: 24px;
        background-image: url('/nuxt-assets/icons/map-ico.svg');
        margin-top: 15px;
        margin-right: 10px;
    }

    &__location {
        text-decoration: underline;
    }

    .badge-success {
        background: #28a745;
    }

    .table-border-bottom {
        font-size: 14px;

        td {
            border-bottom: 1px solid $color-grey-dark;
            border-top: 0;
            padding: 0.3rem;
        }
    }

    .table-success {
        background-color: #c3e6cb;
    }

    &__map-container {
        margin-top: 20px;
        position: relative;
        height: 280px;
        z-index: 1;
    }
}
</style>
